import type { ReactNode } from 'react'
import type { ComboboxOption, ListboxOption } from '@headlessui/react'
import { twMerge } from 'tailwind-merge'
import CheckIcon from 'shared/icons/check-icon'
import type { DataInterface, ExtendedDataType } from './editable-form-select'

export type AsHeadlessType = typeof ComboboxOption | typeof ListboxOption

interface CommonListOptionProps<T extends ExtendedDataType> extends Record<string, unknown> {
  item: T | undefined
  getOptionDataAttribute?: (value?: string) => void
  value?: DataInterface<T>['id'] | DataInterface<T>['id'][]
  ['data-index']?: number
  optionRef?: (node: Element | null) => void
  optionIconRenderer?: (data?: T) => ReactNode | undefined
  firstRect?: boolean
  lastRect?: boolean
  as: AsHeadlessType
}

function CommonListOption<T extends ExtendedDataType>({
  item,
  getOptionDataAttribute,
  value,
  optionRef,
  optionIconRenderer,
  firstRect,
  lastRect,
  as: As,
  ...restProps
}: CommonListOptionProps<T>): ReactNode {
  const caption = item?.customCaption || item?.caption
  const isCaptionEmpty = !caption
  return (
    <As
      ref={optionRef}
      value={item}
      className={({ focus }: { focus: boolean }) =>
        twMerge(
          `group relative flex w-full min-w-0 items-center justify-between border-b border-text-secondary/10 px-4 py-2.5 last:border-b-0 hover:bg-brand-primary hover:text-background-secondary`,
          !lastRect && 'last:rounded-b-lg',
          !firstRect && `first:rounded-t-lg`,
          focus
            ? `bg-brand-primary text-background-secondary`
            : `bg-background-secondary text-text-primary`,
        )
      }
      {...(getOptionDataAttribute ? getOptionDataAttribute(item?.caption) : {})}
      {...restProps}
    >
      {({ focus }: { focus: boolean }) => (
        <div
          className={twMerge(
            `flex w-full min-w-0 items-center justify-between gap-3`,
            isCaptionEmpty && 'text-transparent',
          )}
        >
          <span className="min-w-0 flex-1 break-all">{isCaptionEmpty ? '.' : caption}</span>
          <div className="flex shrink-0 gap-3">
            {(Array.isArray(value) ? value.includes(item?.id) : item?.id == value) && (
              <CheckIcon
                className={twMerge(
                  `size-4 group-hover:stroke-background-secondary`,
                  focus ? 'stroke-background-secondary' : `stroke-brand-primary`,
                )}
                aria-hidden="true"
              />
            )}
            {optionIconRenderer && optionIconRenderer(item)}
          </div>
        </div>
      )}
    </As>
  )
}

export default CommonListOption
